import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, defineMessages, useIntl } from 'react-intl';
import { Panel } from 'primereact/panel';
import { Button } from 'primereact/button';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import { useHistory } from 'react-router-dom';
import { General, EmailTemplates } from './FormSections';
import { createNewTemplate } from '../../../../../services/apps/SurveyTemplateService';
import { getCommunicationTemplateForSurvey, buildEmailTemplatesData } from '../../../../../utils/TemplateUtil';
import { getEntityListWithAccount } from '../../../../../utils/AppUtil';
import Debug from '../../../../../components/Debug';
import { AccountContext } from '../../../../../components/AccountContext';
import { NotificationContext } from '../../../../../components/GrowlNotifications';
import { growlErrors } from '../../../../../utils/NotificationUtil';
import { validationMessages } from '../../../../../i18n/common';
import FormikPickList from '../../../../../components/formik-components/FormikPicklist';
import SurveyTemplateLanguages from './SurveyTempLanguages';
import useSubscriptionExpiredRestrictions from '../../../../../hooks/useSubscriptionExpiredRestrictions';

const messages = defineMessages({
  save: { id: 'actions.save', defaultMessage: 'Save' },
  cancel: { id: 'actions.cancel', defaultMessage: 'Cancel' },
});

const dimensionTemplate = (dimension) => (
  <div className="p-clearfix">
    <div>
      <strong>{dimension.displayName}</strong>
      {` - ${dimension.abilityName}`}
    </div>
  </div>
);

const NewSurveyTemplateForm = ({ data }) => {
  const currentAccount = useContext(AccountContext);
  const growl = useContext(NotificationContext);
  const { formatMessage } = useIntl();
  const history = useHistory();
  const { subscriptionIsExpired } = useSubscriptionExpiredRestrictions();

  const handleCancel = () => {
    const redirectUrl = `/accounts/${currentAccount}/surveys/templates`;
    history.push(redirectUrl);
  };

  const submitNewTemplate = async (formValue) => {
    const { title, abilities } = formValue;
    const { feedbackTemplateId, inviteTemplateId, reminderTemplateId, thankYouTemplateId } = buildEmailTemplatesData(
      formValue,
    );

    const inputAbilities = abilities.map((ability) => ability.abilityId);
    const newTemplateData = {
      accountId: currentAccount,
      feedbackTemplateId,
      inviteTemplateId,
      reminderTemplateId,
      title,
      thankYouTemplateId,
      abilities: inputAbilities,
    };
    try {
      const response = await createNewTemplate(newTemplateData);
      const { id } = response.createSurveyTemplate.template;
      const detailURL = `/accounts/${currentAccount}/surveys/templates/${id}`;
      history.push(detailURL);
    } catch (errors) {
      growlErrors(growl, errors);
    }
  };

  const { surveyCompetencies } = data;
  const communicationTemplates = getEntityListWithAccount(data, 'communicationTemplates');
  const commuTemplates = getCommunicationTemplateForSurvey(communicationTemplates);

  const templateAbilities = [];
  surveyCompetencies.forEach((competency) => {
    competency.abilities.forEach((ability) => {
      templateAbilities.push({
        competencyId: competency.id,
        displayName: competency.displayName,
        abilityId: ability.id,
        abilityName: ability.displayName,
      });
    });
  });

  templateAbilities.sort((a, b) => a.displayName.localeCompare(b.displayName)).slice();

  const initialValues = {
    inviteTemplateId: -1,
    thankYouTemplateId: -1,
    reminderTemplateId: -1,
    feedbackTemplateId: -1,
    title: '',
    abilities: [],
  };
  const validationSchema = Yup.object().shape({
    title: Yup.string().required(formatMessage(validationMessages.required)),
    inviteTemplateId: Yup.string().required(formatMessage(validationMessages.required)),
    thankYouTemplateId: Yup.string().required(formatMessage(validationMessages.required)),
    reminderTemplateId: Yup.string().required(formatMessage(validationMessages.required)),
    feedbackTemplateId: Yup.string().required(formatMessage(validationMessages.required)),
    abilities: Yup.array().required(formatMessage(validationMessages.required)),
  });

  return (
    <div className="p-grid">
      <div className="p-col-12">
        <div className="brand-detailed-wrapper">
          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={async (formValues, { setSubmitting }) => {
              submitNewTemplate(formValues);
              setSubmitting(false);
            }}
          >
            {({ isSubmitting, values, setFieldValue }) => {
              const areAnyAbilitiesSelected = values.abilities.length > 0;
              return (
                <div className="p-grid">
                  <Panel
                    header={<FormattedMessage id="page.survey_template.new" defaultMessage="Add Survey Template" />}
                    className={areAnyAbilitiesSelected ? 'p-col-8' : 'o-col-12'}
                  >
                    <Form>
                      <div className="p-grid">
                        <div className="p-col-12">
                          <General />
                        </div>
                        <div className="p-col-12">
                          <EmailTemplates templates={commuTemplates} />
                        </div>
                        <div className="p-col-12">
                          <Field
                            id="abilities"
                            name="abilities"
                            component={FormikPickList}
                            source={templateAbilities}
                            template={dimensionTemplate}
                            setFieldValue={setFieldValue}
                            idKey="name"
                          />
                        </div>
                        <div className="p-grid">
                          <div className="p-col-12">
                            <Button
                              type="submit"
                              icon={isSubmitting ? 'pi pi-spin pi-spinner' : ''}
                              className="btn btn-sm btn-primary"
                              disabled={isSubmitting || subscriptionIsExpired}
                              label={formatMessage(messages.save)}
                            />
                            <Button
                              type="button"
                              className="p-button-secondary"
                              label={formatMessage(messages.cancel)}
                              onClick={handleCancel}
                            />
                          </div>
                        </div>
                      </div>
                      <Debug />
                    </Form>
                  </Panel>

                  {areAnyAbilitiesSelected && (
                    <SurveyTemplateLanguages formValues={values} surveyCompetencies={surveyCompetencies} />
                  )}
                </div>
              );
            }}
          </Formik>
        </div>
      </div>
    </div>
  );
};

NewSurveyTemplateForm.propTypes = {
  data: PropTypes.objectOf(PropTypes.any).isRequired,
};

export default NewSurveyTemplateForm;
